import { useState } from "react";
import {
  decadeOptions,
  episodeOptions,
  ratingOptions,
  regionOptions,
  sortOptions,
} from "@/constants/filters";

export interface CatalogFilterValues {
  region: string;
  decade: string;
  rating: string;
  episodes: string;
  sort: string;
}

interface CatalogFiltersProps {
  values: CatalogFilterValues;
  showEpisodes: boolean;
  onChange: (key: keyof CatalogFilterValues, value: string) => void;
  onReset: () => void;
  isFiltered: boolean;
}

interface PillGroupProps {
  label: string;
  options: string[];
  value: string;
  onSelect: (value: string) => void;
}

function PillGroup({ label, options, value, onSelect }: PillGroupProps) {
  return (
    <div className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:gap-5">
      <span className="w-16 shrink-0 text-xs text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = option === value;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onSelect(option)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors cursor-pointer ${
                active
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CatalogFilters({
  values,
  showEpisodes,
  onChange,
  onReset,
  isFiltered,
}: CatalogFiltersProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-3 lg:hidden">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex items-center gap-2 whitespace-nowrap text-sm text-foreground-800 cursor-pointer"
        >
          <i className="ri-equalizer-2-line text-primary-600" />
          筛选与排序
          {isFiltered && <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />}
        </button>
        <div className="flex items-center gap-3">
          {isFiltered && (
            <button
              type="button"
              onClick={onReset}
              className="whitespace-nowrap text-xs text-foreground-500 underline-offset-4 hover:text-accent-600 hover:underline cursor-pointer"
            >
              重置
            </button>
          )}
          <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </div>

      <div className={`${open ? "block" : "hidden"} px-4 pb-3 lg:block lg:px-5 lg:py-2`}>
        <div className="divide-y divide-background-200">
          <PillGroup label="地区" options={regionOptions} value={values.region} onSelect={(value) => onChange("region", value)} />
          <PillGroup label="年代" options={decadeOptions} value={values.decade} onSelect={(value) => onChange("decade", value)} />
          <PillGroup label="评分" options={ratingOptions} value={values.rating} onSelect={(value) => onChange("rating", value)} />
          {showEpisodes && (
            <PillGroup
              label="集数"
              options={episodeOptions}
              value={values.episodes}
              onSelect={(value) => onChange("episodes", value)}
            />
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-background-200 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="w-16 shrink-0 text-xs text-foreground-500">排序</span>
            <div className="relative">
              <select
                value={values.sort}
                onChange={(event) => onChange("sort", event.target.value)}
                aria-label="排序方式"
                className="appearance-none rounded-md border border-background-300 bg-background-50 py-1.5 pl-3 pr-8 text-xs text-foreground-800 outline-none transition-colors focus:border-primary-300 cursor-pointer"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-foreground-500" />
            </div>
          </div>

          <button
            type="button"
            onClick={onReset}
            disabled={!isFiltered}
            className="hidden whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40 lg:flex lg:items-center lg:gap-1 cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}